import { CheckoutContext } from '@/context/CheckoutContext';
import { useIsPaymentMethodUnavailable } from '@/context/PaymentMethodAvailabilityContext';
import { useContext } from 'react';
import { SavedCardsForm } from './SavedCardsForm';
import { InitialCreditCardForm } from './InitialCreditCardForm';
import { Disclaimer } from '../Common/Disclaimer';
import { useTranslate } from '@/hooks/useTranslate';
import { useIsBottomSheet } from '@/context/BottomSheetContext';

interface CreditCardFormProps {
  // Suppresses the card title + disclaimer block when card is rendered inside the
  // "Other" dropdown, where OtherPaymentForm already shows the disclaimer —
  // otherwise it appears twice.
  hideDisclaimer?: boolean;
}

export const CreditCardForm = ({ hideDisclaimer = false }: CreditCardFormProps) => {
  const {
    card: { savedCards },
    template,
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  // The swipe-up sheet is a stripped one-tap card: it shows ONLY the saved-card
  // row + pay button (Figma), so the title + Disclaimer trust block are dropped
  // here — they read as chrome on the minimal sheet. The full labelled form is
  // still reachable via "Use Other Payment Method".
  const isBottomSheet = useIsBottomSheet();
  const isUnavailable = useIsPaymentMethodUnavailable();
  // The "Pay with Debit or Credit Card" title anchors the manual card-entry
  // form. It stays on even when card is the primary/auto-selected method
  // (SINGLE / PREFERRED) — it reads as a section label, not redundant noise.
  // Controlled separately from the wallet APM headers (`showApmFormHeader`).
  const showCardFormHeader = template.features.showCardFormHeader && !hideDisclaimer;

  return (
    <div data-testid="credit-card-form" className="flex flex-col gap-2">
      {!hideDisclaimer && !isBottomSheet && !isUnavailable && (
        <div className="flex flex-col gap-2">
          {showCardFormHeader && (
            <div className="text-base text-card-foreground font-semibold">{t('payWithDebitOrCreditCard')}</div>
          )}
          <Disclaimer />
        </div>
      )}
      {savedCards.length ? <SavedCardsForm /> : <InitialCreditCardForm />}
    </div>
  );
};
